import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Ban,
  LinkIcon,
  MessageCircle,
  MoreHorizontal,
  Pin,
  Search,
  UserMinus,
  UserPlus,
  Users,
} from 'lucide-react';
import { useAuth } from '@/app/providers';
import { Avatar, Spinner } from '@/components/ui';
import { toast } from '@/components/feedback/toast';
import { ROUTES } from '@/config/constants';
import { cn } from '@/lib/utils';
import { stripHandle } from '@/features/profile/helpers';
import {
  useBlockUser,
  useFriends,
  useIncomingFriendRequests,
  useOnlineFriendIds,
  useRemoveFriend,
} from '@/features/profile/hooks/useSocial';
import type { FriendProfile } from '@/features/profile/api/social';
import { getOrCreateDm } from './api/directMessages';
import type { PinnedMessage } from './api/pins';
import { useMyPins } from './hooks/usePins';
import { InboxSearch } from './components/InboxSearch';
import { AddFriendSearch } from './components/AddFriendSearch';

export function InboxPage() {
  const navigate = useNavigate();
  const { user, profile, loading: authLoading } = useAuth();
  const { data: friends = [], isLoading } = useFriends(user?.id);
  const { data: requests = [] } = useIncomingFriendRequests(user?.id);
  const friendIds = useMemo(() => friends.map((f) => f.id), [friends]);
  const { data: onlineIds = [] } = useOnlineFriendIds(friendIds);
  const onlineSet = useMemo(() => new Set(onlineIds), [onlineIds]);
  const onlineFriends = useMemo(
    () => friends.filter((f) => onlineSet.has(f.id)),
    [friends, onlineSet],
  );
  const [openingId, setOpeningId] = useState<string | null>(null);
  const removeFriend = useRemoveFriend(user?.id);
  const blockUser = useBlockUser(user?.id);

  const displayName = profile?.display_name || profile?.handle || user?.email || 'Your inbox';

  function back() {
    if (window.history.length > 1) navigate(-1);
    else navigate(ROUTES.home);
  }

  async function openDm(friend: FriendProfile) {
    if (!user) return;
    setOpeningId(friend.id);
    try {
      const conversationId = await getOrCreateDm(friend.id);
      navigate(`/dm/${conversationId}`);
    } catch (err) {
      toast((err as Error).message || 'Could not open message', 'error');
    } finally {
      setOpeningId(null);
    }
  }

  if (!authLoading && !user) {
    return (
      <InboxShell>
        <div className="grid h-full place-items-center bg-black/20 px-6 text-center lg:col-span-2">
          <div className="max-w-[420px]">
            <div className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-full bg-white/[0.06] text-accent">
              <MessageCircle size={28} />
            </div>
            <h1 className="text-3xl font-black tracking-[-0.8px] text-white">Sign in to view Inbox</h1>
            <p className="mt-3 text-sm font-semibold leading-6 text-white/56">
              Your direct messages and friend conversations will show up here once you log in.
            </p>
          </div>
        </div>
      </InboxShell>
    );
  }

  return (
    <InboxShell>
      <section className="relative min-h-0 overflow-y-auto bg-black/20 px-5 pb-8 pt-16 sm:px-8">
        <button
          type="button"
          onClick={back}
          aria-label="Back"
          className="absolute left-4 top-5 z-10 grid h-9 w-9 place-items-center text-white/70 drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)] transition-colors hover:text-white sm:left-7"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="max-w-[1480px]">
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 text-[12px] font-black uppercase tracking-[0.28em] text-accent">
                <MessageCircle size={15} />
                Inbox
              </div>
              <h1 className="m-0 text-[38px] font-black leading-none tracking-[-1px] text-white">
                Messages
              </h1>
              <p className="mt-2 text-sm font-semibold text-white/50">
                Start a direct message with people you are friends with.
              </p>
              <InboxSearch />
            </div>
            <div className="flex items-center gap-2">
              <AddFriendSearch />
              <div className="inline-flex items-center gap-2 rounded-full bg-white/[0.045] px-3 py-2 text-xs font-bold text-white/58">
                <UserPlus size={14} className="text-accent" />
                {requests.length ? `${requests.length} request${requests.length === 1 ? '' : 's'}` : 'No requests'}
              </div>
            </div>
          </div>

          {isLoading || authLoading ? (
            <div className="grid h-[320px] place-items-center rounded-[22px] border border-white/[0.06] bg-white/[0.035]">
              <Spinner />
            </div>
          ) : friends.length ? (
            <FriendSection title="All friends" count={friends.length}>
              {friends.map((friend) => (
                <FriendRow
                  key={friend.id}
                  friend={friend}
                  online={onlineSet.has(friend.id)}
                  opening={openingId === friend.id}
                  onOpen={() => void openDm(friend)}
                  onRemove={() =>
                    removeFriend.mutate(friend.id, { onSuccess: () => toast('Friend removed.') })
                  }
                  onBlock={() =>
                    blockUser.mutate(friend.id, { onSuccess: () => toast('User blocked.') })
                  }
                />
              ))}
            </FriendSection>
          ) : (
            <NoFriendsState />
          )}
        </div>
      </section>

      <InboxInfoRail
        name={displayName}
        avatarUrl={profile?.avatar_url ?? null}
        friends={friends}
        onlineFriends={onlineFriends}
        pendingCount={requests.length}
        onNavigate={(to) => navigate(to)}
        onOpenFriend={(friend) => void openDm(friend)}
      />
    </InboxShell>
  );
}

function InboxShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-[120] overflow-hidden bg-[linear-gradient(180deg,rgba(10,10,14,0.18),rgba(10,10,14,0.3))] backdrop-blur-[22px] backdrop-saturate-[1.3]">
      <div className="absolute inset-0 grid min-h-0 lg:grid-cols-[minmax(0,1fr)_360px]">
        {children}
      </div>
    </div>
  );
}

function FriendSection({
  title,
  count,
  emptyLabel,
  children,
}: {
  title: string;
  count: number;
  emptyLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-2 text-[12px] font-black uppercase tracking-[0.18em] text-white/45">
        <span>{title}</span>
        <span className="text-white/30">{count}</span>
      </div>
      {count ? (
        <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">{children}</div>
      ) : (
        <div className="rounded-[18px] border border-dashed border-white/[0.1] bg-white/[0.02] px-5 py-8 text-center text-[13px] font-semibold text-white/40">
          {emptyLabel}
        </div>
      )}
    </section>
  );
}

function FriendRow({
  friend,
  online,
  opening,
  onOpen,
  onRemove,
  onBlock,
}: {
  friend: FriendProfile;
  online: boolean;
  opening: boolean;
  onOpen: () => void;
  onRemove: () => void;
  onBlock: () => void;
}) {
  const name = friend.display_name || stripHandle(friend.handle) || 'WINR member';
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        'group relative flex w-full cursor-pointer items-center gap-4 rounded-[18px] px-2 py-3 text-left transition-colors hover:bg-white/[0.04]',
        opening && 'cursor-wait opacity-70',
      )}
    >
      <Avatar src={friend.avatar_url} name={name} size={54} className="border border-white/10" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[16px] font-black text-white">{name}</div>
        <div className="mt-1 flex items-center gap-2 text-[12px] font-semibold text-white/42">
          <span className={cn('h-2 w-2 rounded-full', online ? 'bg-[#22c55e]' : 'bg-white/25')} />
          {online ? 'Online' : friend.handle ? `@${stripHandle(friend.handle)}` : 'Offline'}
        </div>
      </div>
      {opening && <Spinner className="h-4 w-4 text-white/50" />}
      <FriendRowMenu name={name} onRemove={onRemove} onBlock={onBlock} />
    </div>
  );
}

function FriendRowMenu({
  name,
  onRemove,
  onBlock,
}: {
  name: string;
  onRemove: () => void;
  onBlock: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex-none" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Friend options"
        className={cn(
          'grid h-9 w-9 place-items-center rounded-full text-white/55 transition-all hover:bg-white/[0.08] hover:text-white',
          open ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
        )}
      >
        <MoreHorizontal size={18} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-[calc(100%+6px)] z-50 w-44 overflow-hidden rounded-lg border border-white/10 bg-[rgba(16,16,22,0.98)] p-1.5 shadow-[0_24px_60px_rgba(0,0,0,0.6)] backdrop-blur-2xl">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                if (window.confirm(`Remove ${name} from your friends?`)) onRemove();
              }}
              className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-[13px] font-semibold text-white/80 transition-colors hover:bg-white/[0.06] hover:text-white"
            >
              <UserMinus size={15} />
              Remove friend
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                if (window.confirm(`Block ${name}? This removes them as a friend.`)) onBlock();
              }}
              className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-[13px] font-semibold text-red-300 transition-colors hover:bg-red-500/10 hover:text-red-200"
            >
              <Ban size={15} />
              Block
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function NoFriendsState() {
  return (
    <div className="rounded-[24px] border border-dashed border-white/[0.12] bg-white/[0.025] px-6 py-16 text-center">
      <div className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-full bg-white/[0.055] text-accent">
        <Search size={26} />
      </div>
      <h2 className="text-2xl font-black tracking-[-0.6px] text-white">No friends yet</h2>
      <p className="mx-auto mt-3 max-w-[440px] text-sm font-semibold leading-6 text-white/52">
        Add friends from member profiles first. Once someone accepts, they will show up here and you
        can start a direct message.
      </p>
    </div>
  );
}

function InboxInfoRail({
  name,
  avatarUrl,
  friends,
  onlineFriends,
  pendingCount,
  onNavigate,
  onOpenFriend,
}: {
  name: string;
  avatarUrl: string | null;
  friends: FriendProfile[];
  onlineFriends: FriendProfile[];
  pendingCount: number;
  onNavigate: (to: string) => void;
  onOpenFriend: (friend: FriendProfile) => void;
}) {
  const { data: pins = [] } = useMyPins();
  return (
    <aside className="hidden min-h-0 flex-col overflow-y-auto border-l border-white/[0.06] bg-[rgba(17,18,24,0.58)] shadow-[0_18px_60px_rgba(0,0,0,0.18)] backdrop-blur-2xl backdrop-saturate-[1.25] lg:flex">
      <div className="relative h-40 flex-none overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(84,185,254,0.26),transparent_35%),linear-gradient(135deg,rgba(255,255,255,0.08),rgba(255,255,255,0.01))]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-black/20 to-[rgba(17,18,24,0.95)]" />
      </div>

      <div className="relative z-[1] -mt-12 flex-none px-5 pb-5">
        <Avatar
          src={avatarUrl}
          name={name}
          size={88}
          className="relative z-[2] border-[4px] border-[#111218] shadow-[0_12px_34px_rgba(0,0,0,0.5)]"
        />
        <h2 className="mt-3 truncate text-[22px] font-black tracking-[-0.4px] text-hi">{name}</h2>
        <p className="mt-1 text-[13px] font-semibold text-white/50">
          {friends.length ? `${friends.length} friend${friends.length === 1 ? '' : 's'} available` : 'Build your friend network'}
        </p>
      </div>

      <div className="min-h-0 flex-1 px-4 pb-5">
        <RailSection title="Online now" count={onlineFriends.length}>
          {onlineFriends.length ? (
            <div className="grid gap-1">
              {onlineFriends.map((friend) => {
                const friendName = friend.display_name || stripHandle(friend.handle) || 'WINR member';
                return (
                  <button
                    key={friend.id}
                    type="button"
                    onClick={() => onOpenFriend(friend)}
                    className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-white/[0.05]"
                  >
                    <span className="relative flex-none">
                      <Avatar
                        src={friend.avatar_url}
                        name={friendName}
                        size={36}
                        className="border border-white/10"
                      />
                      <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[#111218] bg-[#22c55e]" />
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[13px] font-bold text-white/80">
                      {friendName}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <EmptyRail icon={<Users size={16} />}>No friends online right now.</EmptyRail>
          )}
        </RailSection>

        <RailSection title="Friends" count={friends.length} className="mt-6">
          {friends.length ? (
            <div className="grid grid-cols-5 gap-2">
              {friends.slice(0, 20).map((friend) => {
                const friendName = friend.display_name || stripHandle(friend.handle) || 'WINR member';
                return (
                  <Avatar
                    key={friend.id}
                    src={friend.avatar_url}
                    name={friendName}
                    size={54}
                    className="border border-white/10"
                  />
                );
              })}
            </div>
          ) : (
            <EmptyRail icon={<Users size={16} />}>Friends you add will appear here.</EmptyRail>
          )}
        </RailSection>

        <RailSection
          title="Pinned"
          count={pins.length}
          className="mt-6"
          action={
            pins.length ? (
              <button
                type="button"
                onClick={() => onNavigate(ROUTES.inboxPins)}
                className="text-[11px] font-black uppercase tracking-[0.6px] text-accent transition-colors hover:text-white"
              >
                See all
              </button>
            ) : undefined
          }
        >
          {pins.length ? (
            <div className="grid grid-cols-3 gap-2">
              {pins.slice(0, 6).map((pin) => (
                <RailPinTile key={pin.pinId} pin={pin} onOpen={() => onNavigate(`/dm/${pin.conversationId}`)} />
              ))}
            </div>
          ) : (
            <EmptyRail icon={<Pin size={16} />}>Pin a message in any chat to showcase it here.</EmptyRail>
          )}
        </RailSection>

        <RailSection title="Status" count={pendingCount} className="mt-6">
          <EmptyRail icon={<UserPlus size={16} />}>
            {pendingCount
              ? `${pendingCount} incoming friend request${pendingCount === 1 ? '' : 's'} waiting.`
              : 'Friend requests and new conversations will surface here.'}
          </EmptyRail>
        </RailSection>
      </div>
    </aside>
  );
}

function RailSection({
  title,
  count,
  className,
  action,
  children,
}: {
  title: string;
  count: number;
  className?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className={className}>
      <div className="mb-2 flex items-center justify-between text-[11px] font-black uppercase tracking-[0.75px] text-white/45">
        <span>{title}</span>
        {action ?? <span>{count}</span>}
      </div>
      {children}
    </section>
  );
}

function EmptyRail({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.035] p-3 text-[12px] font-medium leading-relaxed text-white/45">
      <span className="text-white/35">{icon}</span>
      <span>{children}</span>
    </div>
  );
}

/** Compact square preview of a pinned message for the rail showcase. */
function RailPinTile({ pin, onOpen }: { pin: PinnedMessage; onOpen: () => void }) {
  const host = (() => {
    if (!pin.link) return null;
    try {
      return new URL(pin.link).hostname.replace(/^www\./, '');
    } catch {
      return pin.link;
    }
  })();
  return (
    <button
      type="button"
      onClick={onOpen}
      title={pin.body ?? host ?? 'Pinned message'}
      className="group relative aspect-square overflow-hidden rounded-lg border border-white/10 bg-white/[0.04] text-left transition-all hover:-translate-y-0.5 hover:border-white/25"
    >
      {pin.mediaUrl ? (
        <img
          src={pin.mediaUrl}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      ) : host ? (
        <div className="flex h-full flex-col items-center justify-center gap-1 px-1 text-center">
          <LinkIcon size={15} className="text-accent" />
          <span className="line-clamp-2 text-[9px] font-bold leading-tight text-white/70">{host}</span>
        </div>
      ) : (
        <p className="line-clamp-4 p-1.5 text-[9px] font-semibold leading-tight text-white/65">
          {pin.body || 'Pinned'}
        </p>
      )}
    </button>
  );
}
